import React, { FC, useMemo } from 'react';
import ChipWithTooltip from '@components/ChipWithTooltip';
import { Wrapper } from '@components/EntityDrawers/drawers/Relationship/components/RelationshipContext/components/RequirementBlock/RequirementBlock.styled';
import RequirementNameInput from '@components/EntityDrawers/drawers/Relationship/components/RelationshipContext/components/RequirementNameInput';
import useBlockControls from '@components/EntityDrawers/drawers/Relationship/components/RelationshipContext/hooks/useBlockControls';
import { RelationOption } from '@components/EntityDrawers/drawers/Relationship/constants/types';
import { Autocomplete } from '@components/Inputs/Autocomplete';
import ListItemWithInfo from '@components/Inputs/Autocomplete/items/ListItemWithInfo';
import { MAX_VISIBLE_CHIP_SYMBOLS } from '@constants/entities/ui';
import { useProject } from '@context/Project/ProjectProvider';
import { Fade, Stack, Switch, Typography } from '@mui/material';
import { transformArrayToOptions } from '@utils/helpers';

type Props = {
  mainLabel: string;
  underLabel: string;
  underPlaceholder: string;
  option: RelationOption;
  childOptions: RelationOption[];
};

const RequirementBlock: FC<Props> = ({
  mainLabel,
  underLabel,
  underPlaceholder,
  option: { id, name },
  childOptions,
}) => {
  const { viewOnly } = useProject();

  const { isOn, values, handleToggle, handleChangeValue } =
    useBlockControls(id);

  const options = useMemo(
    () =>
      transformArrayToOptions(childOptions, {
        label: 'name',
        value: 'id',
      }),
    [childOptions],
  );

  const formattedValues = useMemo(
    () =>
      values.map((value) => {
        const option = options.find((opt) => opt.value === value);

        return {
          label: option?.label ?? value,
          value,
        };
      }),
    [values, options],
  );

  return (
    <Wrapper>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">{mainLabel}</Typography>

        <Switch
          value={isOn}
          checked={isOn}
          disabled={viewOnly}
          onChange={(_, checked) => handleToggle(checked)}
        />
      </Stack>

      <Fade in={isOn} hidden unmountOnExit>
        <Stack gap={4}>
          <RequirementNameInput value={name} />

          <Autocomplete
            label={underLabel}
            placeholder={underPlaceholder}
            options={options}
            value={formattedValues}
            readOnly={viewOnly}
            onChange={(_, newValue) =>
              handleChangeValue(newValue.map((v) => v.value))
            }
            multiple
            itemComponent={ListItemWithInfo}
            renderTags={(value, getTagProps) =>
              value.map(({ label }, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return (
                  <ChipWithTooltip
                    key={key}
                    label={label}
                    tooltip={{
                      disableHoverListener:
                        label.length < MAX_VISIBLE_CHIP_SYMBOLS,
                    }}
                    chip={chipProps}
                  />
                );
              })
            }
          />
        </Stack>
      </Fade>
    </Wrapper>
  );
};

export default RequirementBlock;
